import { Component, Input, OnInit } from "@angular/core"
import { BehaviorSubject, Observable } from "rxjs"
import { NgClass, AsyncPipe } from "@angular/common";
import { SearchableSelectOption } from "src/app/models"
import { SearchableSelectComponent } from "../searchable-select/searchable-select.component"
import { MatIcon } from "@angular/material/icon"

const FILTER_TIME = 600

@Component({
    selector: "valben-multiple-choice-list",
    templateUrl: "./multiple-choice-list.component.html",
    standalone: true,
    imports: [
        NgClass,
        AsyncPipe,
        SearchableSelectComponent,
        MatIcon,
    ],
})
export class MultipleChoiceListComponent implements OnInit {
    @Input() key: string = ""
    @Input() dataObservable!: () => Observable<any> | undefined
    @Input() itemObservable!: () => Observable<any> | undefined
    @Input() update!: (value: any) => void
    // Optional server-paged source. When provided, the dropdown fetches a window of options from the
    // server (search + infinite scroll) instead of showing the whole `dataObservable` list at once.
    // `dataObservable` is still used to resolve the NAMES of already-selected chips.
    @Input() loadPage?: (search: string, skip: number, take: number) => Observable<{ items: any[]; total: number }>

    // Debounce lives in the select; kept at the same delay the local timer used to run at.
    readonly filterTime = FILTER_TIME

    showOptions: boolean = false
    showNewOptionButton: boolean = true

    selectedOptions: { masterId: string; name: string }[] = []
    options: Map<string, string> = new Map<string, string>()
    pickedId: string | null = null
    optionsFiltered$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([])

    /** What the dropdown renders — the loaded server window in paged mode, the whole filtered list otherwise. */
    selectOptions: SearchableSelectOption<string>[] = []

    // Lazy (server-paged) state. `lazyOptions` holds ONLY the real items loaded so far and grows one window
    // at a time as the user scrolls to the end. Never pre-sized with placeholders — placeholder/null slots
    // both paint blank rows, which is what made the dropdown look wrong.
    // ponytail: search + infinite scroll now ride on the shared select's CDK viewport — verify live, then drop.
    lazyOptions: any[] = []
    private _search: string = ""
    private readonly _take: number = 10
    private _total: number = 0
    private _loading: boolean = false

    // Drives the little spinner shown in the dropdown while a window is being fetched.
    get pageLoading(): boolean {
        return this._loading
    }

    get serverSide(): boolean {
        return !!this.loadPage
    }

    add(id: string | null): void {
        this.pickedId = null
        if (!id) return

        // Dedupe: in paged mode already-selected items aren't stripped from the dropdown, so guard against
        // adding the same category twice.
        const opt = Array.from(new Set([...this.selectedOptions.map(o => o.masterId), id]))
        this.update(opt)
    }

    private _toOption(raw: any): { masterId: string; name: string } {
        return { masterId: raw.masterId, name: raw?.names?.["hr"] ?? raw?.name ?? "" }
    }

    private _toSelectOptions(source: { masterId: string; name: string }[]): SearchableSelectOption<string>[] {
        return source.map(o => ({ value: o.masterId, label: o.name, searchText: o.name }))
    }

    // Fetch one window. `replace` = fresh list (panel open / new search); otherwise append the next window.
    // Responses whose search no longer matches the current one are dropped, so a slow in-flight request
    // can't clobber a newer search.
    private _loadWindow(skip: number, replace: boolean): void {
        if (!this.loadPage) return
        const search = this._search
        this._loading = true
        this.loadPage(search, skip, this._take).subscribe({
            next: res => {
                if (search !== this._search) {
                    this._loading = false
                    return
                }
                this._total = res.total
                const opts = res.items.map(it => this._toOption(it))
                this.lazyOptions = replace ? opts : [...this.lazyOptions, ...opts]
                this.selectOptions = this._toSelectOptions(this.lazyOptions)
                this._loading = false
            },
            error: () => (this._loading = false),
        })
    }

    /** Debounced by the select; also fires once with "" when the panel opens, which loads the first window. */
    onSearch(query: string): void {
        if (!this.loadPage) return
        this._search = query
        this._loadWindow(0, true)
    }

    onScrolledToEnd(): void {
        if (!this.loadPage) return
        if (this.lazyOptions.length < this._total) this._loadWindow(this.lazyOptions.length, false)
    }

    deleteOptionOnClick(index: number): void {
        const opt = this.selectedOptions.filter((_, i) => i !== index).map(opt => opt.masterId)
        this.update(opt)
    }

    newOptionOnClick(): void {
        this.showOptions = !this.showOptions
        this.showNewOptionButton = !this.showNewOptionButton
    }

    cancelOnClick(): void {
        this.showOptions = !this.showOptions
        this.showNewOptionButton = !this.showNewOptionButton
    }

    ngOnInit(): void {
        this.optionsFiltered$.subscribe((opts: any[]) => {
            if (!this.loadPage) this.selectOptions = this._toSelectOptions(opts)
        })
        this.dataObservable()?.subscribe((data: any) => {
            this.options.clear()
            data.map((option: any) => {
                this.options.set(option.masterId, option.names["hr"] || "")
            })
            const selectedOptionsCopy = JSON.parse(JSON.stringify(this.selectedOptions))
            this.selectedOptions = this.selectedOptions.filter((opt: any) => {
                return this.options.has(opt.masterId)
            })
            if (selectedOptionsCopy.length !== this.selectedOptions.length) {
                this.update(this.selectedOptions.map(opt => opt.masterId))
            }
            this.optionsFiltered$.next(
                data
                    .map((option: any) => {
                        return { masterId: option.masterId, name: option.names["hr"] || "" }
                    })
                    .filter((option: any) => {
                        return !this.selectedOptions.map(opt => opt.masterId).includes(option.masterId)
                    })
            )
        })
        this.itemObservable()?.subscribe((item: any) => {
            if (item !== null && item !== undefined) {
                this.selectedOptions = item[this.key].map((opt: string) => {
                    return { masterId: opt, name: this.options.get(opt) || "" }
                })
                const opt: { masterId: string; name: string }[] = []
                for (const [key, value] of this.options) {
                    if (!this.selectedOptions.map(opt => opt.masterId).includes(key)) {
                        opt.push({ masterId: key, name: value })
                    }
                }
                this.optionsFiltered$.next(
                    opt.map((option: any) => {
                        return { masterId: option.masterId, name: option.name }
                    })
                )

                if (item[this.key].length === 0) {
                    this.reset()
                }
            }
        })
    }

    private reset(): void {
        this.selectedOptions = []
        this.pickedId = null
        this.showOptions = false
        this.showNewOptionButton = true
    }
}
